/**
 * The pre-production marker.
 *
 * Connect's hostnames are live and publicly reachable long before the product
 * is. Anybody who is sent a link — the customer, a colleague, somebody testing
 * on their phone — sees something that looks finished, and reports bugs against
 * it, and forms opinions from it. This says what it is.
 *
 * **It is driven by the build, not by the hostname.** `VITE_DEPLOY_STAGE` is set
 * by the deploy workflow from a repository variable, so going live is changing
 * one value and re-running the deploy: no code change, no review, nothing to
 * forget to revert. Keying it off the hostname instead would mean a build could
 * not be moved between environments without lying about one of them.
 *
 * **A corner badge, not a top strip.** The admin chrome has a sidebar that is
 * `fixed inset-y-0` below the `md` breakpoint and headers that are
 * `sticky top-0`. A full-width bar in normal flow desynchronises from both —
 * the sidebar ignores it and the sticky header slides over it on the first
 * scroll — so the marker would be intermittently invisible on exactly the small
 * screens where it is most likely to be mistaken for the real thing. This sits
 * outside the layout entirely and cannot be scrolled away from.
 *
 * Renders nothing at all in production, so it costs a deployed build one
 * string comparison.
 */

/** The stages a build can declare. Anything else is treated as production. */
export type DeployStage = 'pre-prod' | 'production';

/**
 * What this build was built as.
 *
 * `import.meta.env` is substituted by Vite at BUILD time, so this is a literal
 * in the bundle rather than a runtime lookup — which is why the value cannot
 * drift from the artefact it was built into.
 */
export function deployStage(): DeployStage {
  return import.meta.env.VITE_DEPLOY_STAGE === 'pre-prod' ? 'pre-prod' : 'production';
}

export function DeploymentBanner() {
  if (deployStage() !== 'pre-prod') return null;

  return (
    <div
      // `status`, not `alert`: an alert interrupts a screen reader mid-sentence
      // and this is standing context, not an event. Announced once when the
      // page loads, which is when it is worth knowing.
      role="status"
      aria-label="This is a pre-production installation"
      className={
        'fixed bottom-3 left-3 z-banner select-none rounded-md ' +
        'border border-amber-500/60 bg-amber-400/95 px-2.5 py-1 ' +
        'text-[11px] font-semibold uppercase tracking-wider text-amber-950 ' +
        'shadow-lg shadow-black/20 ' +
        // Never captures a click. It sits over the corner of the viewport, and
        // a marker that swallows a button underneath it is a bug report about
        // the product rather than about the marker.
        'pointer-events-none'
      }
    >
      Pre-production
    </div>
  );
}
